import { useState } from "react";
import { useNavigate } from "react-router-dom";

import * as LocalOperations from "@/Common/Operations/operations.local";
import { PharmacyOrdersClient } from "../Api/PharmacyOrdersClient";
import { PharmacyOrderModel } from "../Models/PharmacyOrderModel";
import { PharmacyOrdersFilterSettingsModel } from "../Models/PharmacyOrdersFilterSettingsModel";

interface IUsePharmacyOrderPagination {
    totalPharmacyOrders: number;
    absoluteIndex: number;
    isPreviousButtonDisabled: boolean;
    onPreviousPharmacyOrder: () => void;
    onNextPharmacyOrder: () => void;
}

function usePharmacyOrderPagination(orderId: string): IUsePharmacyOrderPagination {
    const navigate = useNavigate();

    const [pharmacyOrderIds, setPharmacyOrderIds] = useState<string[]>(pharmacyOrdersStore.pharmacyOrdersIds ?? []);
    const [currentInboxPageNumber, setCurrentInboxPageNumber] = useState(pharmacyOrdersStore.pharmacyOrdersTableSettings.currentPageNumber ?? 0);
    const [loading, setLoading] = useState(false);

    const totalPharmacyOrders = pharmacyOrdersStore.totalPharmacyOrders ?? 0;
    const currentOrderIndex = pharmacyOrderIds.findIndex(p => p === orderId) ?? 0;
    const getOrderIdByIndex = (index: number): string => pharmacyOrderIds[index];
    const absoluteIndex = currentOrderIndex + pharmacyOrdersStore.pharmacyOrdersFilterSettings.queryPageSize * ((currentInboxPageNumber ?? 1) - 1) + 1;

    const isOrderLastIndex: boolean = pharmacyOrderIds.length - 1 === currentOrderIndex;

    const isPreviousButtonDisabled = currentInboxPageNumber === 1 && currentOrderIndex === 0;

    const handleNavigation = (orderId: string): void => {
        navigate(`/orders/${orderId}`);
    };

    const onPreviousPharmacyOrder = async (): Promise<void> => {
        if (loading) return;
        setLoading(true);

        if (currentInboxPageNumber > 1 && currentOrderIndex === 0) {
            const currentPage = currentInboxPageNumber - 1;
            updatePharmacyOrdersIds(currentPage)
                .then(updatedPharmacyOrderIds => {
                    updateCurrentPageNumber(currentPage);
                    handleNavigation(updatedPharmacyOrderIds[updatedPharmacyOrderIds.length - 1]);
                });
        }
        else {
            handleNavigation(getOrderIdByIndex(currentOrderIndex - 1));
        }
        setLoading(false);
    };

    const onNextPharmacyOrder = async (): Promise<void> => {
        if (loading) return;
        setLoading(true);

        const inboxPageCount = Math.floor((totalPharmacyOrders / pharmacyOrdersStore.pharmacyOrdersFilterSettings.queryPageSize) + 1);
        if (currentInboxPageNumber < inboxPageCount && isOrderLastIndex) {
            const currentPage = currentInboxPageNumber + 1;
            await updatePharmacyOrdersIds(currentPage)
                .then(updatedPharmacyOrderIds => {
                    updateCurrentPageNumber(currentPage);
                    handleNavigation(updatedPharmacyOrderIds[0]);
                });
        }
        else if (isOrderLastIndex) {
            navigate("/orders");
        }
        else {
            handleNavigation(getOrderIdByIndex(currentOrderIndex + 1));
        }
        setLoading(false);
    };

    const updatePharmacyOrdersIds = async (currentPageNumber: number): Promise<string[]> => {
        const response = await PharmacyOrdersClient.fetchPharmacyOrders({
            FilterSettings: PharmacyOrdersFilterSettingsModel.toResponseModel(pharmacyOrdersStore.pharmacyOrdersFilterSettings),
            QueryCurrentPageNumber: currentPageNumber,
            QueryOrderDirection: pharmacyOrdersStore.pharmacyOrdersTableSettings.queryOrderDirection,
            QueryOrderType: pharmacyOrdersStore.pharmacyOrdersTableSettings.queryOrderType,
            SearchText: pharmacyOrdersStore.pharmacyOrdersTableSettings.searchText
        });

        if (response) {
            const updatedPharmacyOrderIds = response.PharmacyOrders.map(p => PharmacyOrderModel.fromResponseModel(p)).map(p => p.orderId);
            setPharmacyOrderIds(updatedPharmacyOrderIds);
            return updatedPharmacyOrderIds;
        }
        else {
            setPharmacyOrderIds([]);
            return [];
        }
    };

    const updateCurrentPageNumber = (currentPageNumber: number): void => {
        sessionStore.dispatch(LocalOperations.PharmacyOrders.SetTableSettings, { tableSetting: { ...pharmacyOrdersStore.pharmacyOrdersTableSettings, currentInboxPage: currentPageNumber } });
        setCurrentInboxPageNumber(currentPageNumber);
    };

    return {
        totalPharmacyOrders,
        absoluteIndex,
        isPreviousButtonDisabled,
        onPreviousPharmacyOrder,
        onNextPharmacyOrder
    };
}

export default usePharmacyOrderPagination;
